import { Component, computed, input, output, linkedSignal, effect, ElementRef, viewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { FieldTrackingConfig } from 'src/app/config/eev-form-tracking.config';
import { AuthEventType } from 'src/app/enums/event/event.enum';
import { FieldError, FieldInteraction } from 'src/app/models/event-data.model';
import { EventTrackingService } from 'src/app/services/event-tracking.service';

@Component({
  selector: 'app-be-input-group-reactive',
  templateUrl: './be-input-group-reactive.component.html',
  styleUrls: ['./be-input-group-reactive.component.scss'],
  standalone: false,
})
export class BeInputGroupReactiveComponent {
  readonly beFormGroup = input.required<UntypedFormGroup>();
  readonly beFormControl = input.required<UntypedFormControl>();
  readonly beFormControlName = input.required<string>();
  readonly inputLabel = input.required<string>();
  readonly inputType = input<string>('text');
  readonly isHidingText = input<boolean>(false);
  readonly inputPlaceholder = input<string>();
  readonly requiredError = input<string>();
  readonly detailError = input<string>();
  readonly hint = input<string>();
  readonly maskValue = input<string>();
  readonly specialCharactersValues = input<string[]>([
    `-`,
    `/`,
    `(`,
    `)`,
    `.`,
    `:`,
    `space`,
    `+`,
    `,`,
    `@`,
    `[`,
    `]`,
    `"`,
    `'`,
  ]);
  readonly patternsValue = input<any>();
  readonly inputMaxLength = input<string>();
  readonly describedBy = input<string>();
  readonly variant = input<'sm' | 'md' | 'lg'>();
  readonly autocompleteValue = input<'on' | 'off'>('off');
  readonly inputName = input<string>();
  readonly inputMode = input<'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url'>();
  readonly errorTrackingSubject = input<Subject<FieldError>>();
  readonly eevTrackingConfig = input<FieldTrackingConfig>();

  readonly onFocus = output<void>();
  readonly onBlur = output<void>();

  readonly hintId = computed(() => (this.beFormControlName() ? `${this.beFormControlName()}Hint` : undefined));
  readonly requiredErrorId = computed(() =>
    this.beFormControlName() ? `${this.beFormControlName()}RequiredError` : undefined
  );
  readonly detailErrorId = computed(() =>
    this.beFormControlName() ? `${this.beFormControlName()}DetailError` : undefined
  );

  //  Parent controls default hidden state; user can override via toggle.
  readonly isHiddenInput = linkedSignal<boolean>(() => this.isHidingText() || this.inputType() === 'password');
  // Show toggle button when password OR parent wants hidden by default.
  readonly isHiddenTogglable = computed(() => this.inputType() === 'password' || this.isHidingText());
  // Only password inputs switch between text/password.
  readonly effectiveInputType = computed(() => {
    const inputType = this.inputType();
    const isHiddenInput = this.isHiddenInput();
    if (inputType !== 'password') return inputType;
    return isHiddenInput ? 'password' : 'text';
  });

  inputRef = viewChild.required<ElementRef<HTMLInputElement>>('input');

  constructor(private eventTrackingService: EventTrackingService) {
    const firstInteractionEffect = effect(
      () => {
        const control = this.beFormControl();
        const config = this.eevTrackingConfig();
        if (!control || !config) return;
        control.valueChanges.pipe(take(1)).subscribe(() => {
          this.eventTrackingService.trackEvent<FieldInteraction>(AuthEventType.FIELD_INTERACTION, config.event);
          firstInteractionEffect.destroy();
        });
      },
      { manualCleanup: true }
    );
  }

  toggleTextVisibility() {
    this.isHiddenInput.update((v) => !v);
    this.focusInput();
  }

  onFocusLoss() {
    this.onBlur.emit();
    const config = this.eevTrackingConfig();
    const control = this.beFormControl();
    if (!config || !control.touched || !control.dirty) return;
    const requiredError = this.requiredError();
    const error: FieldError =
      requiredError && control.hasError('required')
        ? { ...config.event, authFormFieldErrorMessage: requiredError }
        : config.errorFn(control);
    const subject = this.errorTrackingSubject();
    if (error && subject) subject.next(error);
  }

  focusInput() {
    const inputEl = this.inputRef().nativeElement;
    inputEl.focus();

    const len = inputEl.value?.length ?? 0;
    inputEl.setSelectionRange(len, len);
  }
}
